import { useState, useEffect } from "react"

import { useAppDispatch, useAppSelector } from "hooks/store"
import Cookies from "js-cookie"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { personalForm } from "store/auth/auth.selectors"
import {
  setAuthData,
  setActivePageSignUp,
  setPersonalForm,
} from "store/auth/auth.slice"
import { IAuthData } from "ts/interfaces/Auth"
import { getAuthCookieDomain } from "utils/domain"

export type OpenIdSSOProvider = "teachhub" | "classlink"

interface UseOpenIdSSOProps {
  provider: OpenIdSSOProvider
  userType: "teacher" | "student"
  isSignup?: boolean
}

const ssoParamByProvider: Record<OpenIdSSOProvider, string> = {
  teachhub: "openid",
  classlink: "classlink",
}

// The ?error=email_missing / ?error=wrong_user_type / ?error=authentication_failed
// query params are intentionally not handled here: useCleverSSO already renders
// a matching overlay for those same codes on both the teacher and student
// screens, so re-detecting them here would stack a second overlay on top of it.
export const useOpenIdSSO = ({
  provider,
  userType,
  isSignup = false,
}: UseOpenIdSSOProps) => {
  const [isOpenIdSSOLoading, setIsOpenIdSSOLoading] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const currentPersonalForm = useAppSelector(personalForm)

  useEffect(() => {
    const isOpenIdSSO = searchParams.get("sso") === ssoParamByProvider[provider]

    if (isOpenIdSSO) {
      setIsOpenIdSSOLoading(true)
    }
  }, [searchParams, provider])

  const clearOpenIdSearchParams = () => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete("sso")
    newSearchParams.delete("error")
    navigate(
      `${location.pathname}${
        newSearchParams.toString() ? `?${newSearchParams.toString()}` : ""
      }`,
      { replace: true }
    )
  }

  const handleOpenIdReset = () => {
    setIsOpenIdSSOLoading(false)
    clearOpenIdSearchParams()
  }

  const handleOpenIdAuthSuccess = (authData: IAuthData) => {
    dispatch(setAuthData(authData))
    Cookies.remove("auth_data", { domain: getAuthCookieDomain() })
    setIsOpenIdSSOLoading(false)
    clearOpenIdSearchParams()

    if (authData.finalized) {
      navigate(RoutePath.HOMEPAGE, { replace: true })
    } else if (userType === "teacher") {
      dispatch(
        setPersonalForm({
          ...currentPersonalForm,
          first_name: authData.first_name ?? currentPersonalForm.first_name,
          last_name: authData.last_name ?? currentPersonalForm.last_name,
        })
      )
      if (!isSignup) {
        navigate(RoutePath.SIGNUP)
      }
      dispatch(setActivePageSignUp(2))
    } else {
      if (!isSignup) {
        navigate(RoutePath.SIGNUP, { state: "login" })
      }
      dispatch(setActivePageSignUp(3))
    }
  }

  return {
    isOpenIdSSOLoading,
    handleOpenIdReset,
    handleOpenIdAuthSuccess,
  }
}
